import { useRoute, useRouter } from 'vue-router';
import { flows } from '@/parcours/flows';
import { flowStore, resetFlow, setFlowStep, updateFlowData } from '@/parcours/stores/flowStore';
import { ref } from 'vue';

export function useFlow(flowId: string) {
  const router = useRouter();
  const route = useRoute();
  const isSubmitting = ref(false);

  const flow = flows[flowId];

  function go(step: string) {
    setFlowStep(step);
    router.push({
      name: `flow-${flowId}-${step}`,
      params: {
        missionId: route.params.missionId,
        slotId: route.params.slotId,
        reservationId: route.params.reservationId,
      },
    });
  }

  function next(currentStep: string) {
    const transition = flow.transitions[currentStep];

    if (!transition) return;

    let nextStep = transition.next;

    if (transition.guard) {
      const ok = transition.guard(flowStore.data);
      if (!ok) return;
    }

    if (transition.skip) {
      const shouldSkip = transition.skip(flowStore.data);
      if (shouldSkip) {
        nextStep = transition.skipTo;
      }
    }

    // ✅ Support des fonctions dynamiques
    if (typeof nextStep === 'function') {
      const result = nextStep({ data: flowStore.data });

      if (typeof result === 'string') {
        go(result);
      } else if (result?.submit) {
        result.action?.();
        if (result.redirect) router.push(result.redirect);
      }
      return;
    }

    if (nextStep) {
      go(nextStep);
    }
  }
  function back(currentStep: string) {
    const transition = flow.transitions[currentStep];

    if (!transition?.back) return;

    go(transition.back);
  }

  function update(values: Record<string, any>) {
    updateFlowData(values);
  }

  async function submit(currentStep: string) {
    if (isSubmitting.value) return; // 🚫 anti double click

    const payload = flowStore.data;
    const transition = flow.transitions?.[currentStep];

    isSubmitting.value = true;

    try {
      if (flow.onSubmit) {
        await flow.onSubmit(payload);
      }

      const successPath = flow.successRedirect?.(payload);
      resetFlow();
      if (successPath) {
        router.replace(successPath);
      } else {
        go(transition?.success || 'end');
      }
    } catch (error) {
      console.error(error);
      go(transition?.error || 'bad-end');
    } finally {
      isSubmitting.value = false;
    }
  }

  return {
    next,
    back,
    update,
    submit,
    isSubmitting,
  };
}
